<script>
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
</script>

<Section backgroundColor="white" id="toc">
	<h2 class="!text-center">Window Insert Measuring Made Easy</h2>
	<p class="text-left">
		Indow window inserts are custom-made and solve all kinds of issues from
		condensation to noise penetration. Want to know more? Read our answers to
		frequently asked questions. If you don't recognize a word, check out our
		<a href="/resources/faq/interior-storm-window-inserts-glossary">
			glossary of window insert terms
		</a>
		. If you've already purchased your insert and need help measuring, installing,
		or caring for it, visit our
		<a href="/about-indow/care/installation-resources">
			customer resource page
		</a>
		.
	</p>

	<nav>
		<ul class="w-full">
			<li>
				<a href="#whybuy">Why Buy Indow Inserts?</a>
			</li>
			<li>
				<a href="#design">Design and Function</a>
			</li>
			<li>
				<a href="#benefits">Benefits and Performance</a>
			</li>
			<li>
				<a href="#durability">Durability</a>
			</li>
			<li>
				<a href="#wheretheywork">Where They Work and Where They Do Not Work</a>
			</li>
			<li>
				<a href="#purchasing">Purchasing / How To Order</a>
			</li>
			<li>
				<a href="#shipping">Shipping</a>
			</li>
			<li>
				<a href="#upkeepandcare">Upkeep and Care</a>
			</li>
			<li>
				<a href="#odors">Odors</a>
			</li>
			<li>
				<a href="#taxcredit">Tax Credit Information</a>
			</li>
		</ul>
	</nav>
</Section>

<hr />

<Section backgroundColor="white" id="whybuy">
	<h2>Why Buy Indow Inserts?</h2>
	<dl>
		<dt><p>Q: Why use Indow inserts over purchasing new windows?</p></dt>
		<dd>
			<p>
				With Indow, no construction is needed to increase insulation,
				efficiency, and soundproofing. Original windows match a home's character
				and original design. Original windows often have old-growth wood. This
				rare, dense wood helps with insulation. The windows that come with your
				home are difficult to replicate without spending a lot of money.
			</p>
		</dd>
	</dl>
	<dl>
		<dt>
			<p>
				Q: I have old, drafty single pane windows, can Indow inserts help or do
				I have to buy new windows?
			</p>
		</dt>
		<dd>
			<p>
				Indow window inserts can help! Adding Indow inserts will boost your
				current window performance to the equivalence of standard double-pane
				window
				<a href="/performance">performance</a>
				at about 15-20% of the cost.
			</p>
		</dd>
	</dl>
	<nav>
		<ul class="flex list-none flex-col items-center">
			<li>
				<a href="#toc">Return to FAQ Table of Contents</a>
			</li>
			<li>
				<a href="/how-to/get-indow-inserts">Get A Free Estimate</a>
			</li>
		</ul>
	</nav>
</Section>

<hr />

<Section backgroundColor="white" id="design">
	<h2>Design and Function</h2>
	<dl>
		<dt><p>Q: How do Indow inserts stay in place?</p></dt>
		<dd>
			<p>
				Indow window inserts are held in place with our
				<a href="/resources/blog/magnetic-seal-vs-compression-seal">
					patented Compression Tube
				</a>
				which expands into the space between the custom cut acrylic panel and the
				window frame.
			</p>
		</dd>
	</dl>
	<dl>
		<dt>
			<p>Q: How do I remove and re-install Indow inserts? Is this difficult?</p>
		</dt>
		<dd>
			<p>
				Indow inserts
				<a
					href="https://www.youtube.com/watch?v=TNTM7OuuYWw"
					target="_blank"
					rel="external"
				>
					remove and install easily
				</a>
				. Remove by pulling the D-ring and unhooking the safety chain (if present).
				Re-install by lining up each corner with the window frame and pressing insert
				into place. Tip: Put the bottom corners in ﬁrst to align the Indow, then
				the top corners. Once corners are in place,
				<a href="/about-indow/how-to-order/measure">gently push the sides in</a>
				. If the corners of your inserts aren't sitting flush with the window frame,
				watch our video on
				<a
					href="https://www.youtube.com/watch?v=8lBF9O11GS4"
					target="_blank"
					rel="external"
				>
					easy corner adjustments
				</a>
				.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: Can you mount window inserts on the outside?</p></dt>
		<dd>
			<p>
				Indow inserts are made for the interior of your home. You will not
				receive any benefit from mounting the inserts, nor will they fit, on the
				outside of your house.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: Do you make Indow inserts for screen doors?</p></dt>
		<dd>
			<p>Indow does not currently manufacture inserts for screen doors.</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: What colors do Indow inserts come in?</p></dt>
		<dd>
			<p>
				The insert compression tubing currently comes in
				<a href="/about-indow/how-to-order">white, brown and black</a>
				. The brown is comparable to Benjamin Moore's French Press. For large commercial
				jobs, custom colors are available in any of the Pantone colors.
				<br />
				Indow insert panels are clear and transparent except for: Privacy Grade is
				translucent and Sleep Panel is opaque
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: Why do you use acrylic over film or glass?</p></dt>
		<dd>
			<p>
				Acrylic is strong, light weight, and insulates very well. Acrylic has
				superior noise blocking properties over film and is a better insulator
				than glass. Most films appear cloudy and are easily pierced by sharp
				objects. Glass is heavy and can shatter or chip easily. Indow inserts,
				made from acrylic sheets, are far easier to handle and store than glass.
			</p>
		</dd>
	</dl>
	<dl>
		<dt>
			<p>
				Q: Acrylic glazing? I'm concerned with how acrylic will look in my
				window frame.
			</p>
		</dt>
		<dd>
			<p>
				Acrylic is highly transparent and allows more light transmission than
				glass. When Indow inserts are installed, they are almost unnoticeable.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: How noticeable are Indow inserts?</p></dt>
		<dd>
			<p>
				Unlike cloudy film glazes and bulky framed interior storm windows, Indow
				inserts are frameless, composed of highly transparent acrylic glazing
				and practically disappear from sight when installed into a window frame.
				This is all because of our proprietary measurement software. It checks
				the accuracy of your window measurements as they are entered, allowing
				for a level of precision that ensures our compression tubing can have an
				extremely low profile.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: What are the Safety Chains used for?</p></dt>
		<dd>
			<p>
				<a
					href="https://www.youtube.com/watch?v=TNTM7OuuYWw"
					target="_blank"
					rel="external"
				>
					Safety chains
				</a>
				are used in the event of high wind pressure from the outside of leaky exterior
				windows. Indow inserts create such a tight fit that air pressure can build
				up and work to push the insert out. They are low-profile ball chains that
				are hidden in the air pocket between your Indow insert and primary window.
				During high wind events, some of your Indow inserts might vent air pressure
				in the corner or along a side. This is normal and you can simply push the
				corner or side back into place. The safety chains are designed to prevent
				the Indow from falling completely out of the window frame, which is unlikely
				to happen.
			</p>
		</dd>
	</dl>
	<dl>
		<dt>
			<p>
				Q: What if I want to open any of my windows while Indow inserts are
				installed?
			</p>
		</dt>
		<dd>
			<p>
				You can easily remove Indow inserts to open your window and re-install
				it just as easily. You, depending on what type of window you have, can
				also use
				<a
					href="https://www.youtube.com/watch?v=TNTM7OuuYWw"
					target="_blank"
					rel="external"
				>
					safety chains
				</a>
				to have your insert lean part way out and open your window with the insert
				still installed.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: Will the inserts trap condensation?</p></dt>
		<dd>
			<p>
				Indow window inserts should greatly reduce or entirely
				<a href="/resources/case-studies/stop-window-condensation">
					eliminate condensation
				</a>
				on existing single- or double-pane windows. The Compression Tube effectively
				stops warm, moist air from reaching the glass. Most people find they can
				see outside their kitchens for the first time during dinner in winter. There
				will be times when the acrylic will start to fog up but not nearly as severe
				as the glass would. If condensation is repeatedly appearing inside the glass
				or fogging up the acrylic, then there is most likely an existing condition
				in the wall that should be addressed and remedied.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: How do I tell which Indow insert goes into which window?</p></dt>
		<dd>
			<p>
				We use a small label to mark the backside of the bottom left corner of
				each Indow insert to denote which window frame the Indow corresponds to.
				You will create the naming system during the measuring process.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: How do I tell if I got Acoustic or Standard Grade?</p></dt>
		<dd>
			<p>
				Acoustic Grade inserts are 1/4″ thick where Standard Grade inserts are
				1/8″ thick. You can measure your insert using a caliper. Acoustic Grade
				inserts also have a groove along the edge of the tubing. You can view
				both of these
				<a
					href="https://www.youtube.com/watch?v=wPfEkiwr6O0"
					target="_blank"
					rel="external"
				>
					distinguishing methods here
				</a>
				.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: Is there a piece missing from the corner of my frame?</p></dt>
		<dd>
			<p>
				The backside of our inserts have
				<a href="/resources/blog/window-inserts-cut-for-compression">
					cut out corners
				</a>
				on purpose. The corners are compressed from two sides at once and these cutouts
				allow the corner to roll back into the frame properly. The exposed side should
				always face the original window so your insert fits the way it was measured
				for your window frame.
			</p>
		</dd>
	</dl>
	<nav>
		<ul class="flex list-none flex-col items-center">
			<li>
				<a href="#toc">Return to FAQ Table of Contents</a>
			</li>
			<li>
				<a href="/how-to/get-indow-inserts">Get A Free Estimate</a>
			</li>
		</ul>
	</nav>
</Section>

<hr />

<Section backgroundColor="white" id="benefits">
	<h2>Benefits and Performance</h2>
	<dl class="w-full">
		<dt><p>Q: What type of benefits/savings do Indow inserts provide?</p></dt>
		<dd class="flex flex-col">
			<p>Indow window inserts provide the following benefits:</p>
			<ul>
				<li>
					Energy savings equal to a standard double-pane windows at 15% – 20% of
					the cost
				</li>
				<li>
					Nearly disappear when installed, allowing your home's own beauty to
					shine through
				</li>
				<li>Preserve existing windows, which match home's design</li>
				<li>
					<a href="/solutions/noise-performance">Window noise reduction</a>
					of 50% – 70% when placed over single-pane windows
				</li>
				<li>
					<a href="/solutions/noise-performance">Window noise reduction</a>
					of 33% – 50% when placed over double-pane windows*
				</li>
				<li>Easy installation and removal for cleaning or storage</li>
				<li>
					Installation for most spaces can be done in less than two hours,
					without any construction
				</li>
				<li>Saves 20% on energy bills</li>
				<li>
					Enhances your home's resale value, preserving the initial investment
				</li>
			</ul>
		</dd>
	</dl>

	<dl>
		<dt>
			<p>
				Q: I live in a noisy urban area; will Indow inserts help reduce any of
				the noise?
			</p>
		</dt>
		<dd>
			<p>
				<a href="/custom-storm-windows/standard-grade">Standard Grade</a>
				Indow inserts will reduce noise coming through single-pane windows by up
				to 50%,
				<a href="/solutions/window-noise-reduction">Acoustic Grade</a>
				inserts will reduce noise by up to 70% when placed over single-pane windows.
				Acoustic Grade inserts reduce noise coming through double-pane windows by
				50%. This is one of the most important benefits for many of our customers!
				Watch a quick
				<a href="/solutions/window-noise-reduction">demonstration</a>
				of Indow inserts soundproofing abilities.
			</p>
		</dd>
	</dl>
	<dl>
		<dt>
			<p>Q: Will Indow inserts add any benefits to double-pane windows?</p>
		</dt>
		<dd>
			<p>
				Indow inserts, added to your double-pane windows, will give you
				triple-pane performance by boosting efficiency, blocking drafts, and
				significantly reducing noise. Our Acoustic Grade inserts will reduce the
				amount of noise coming through double-pane windows by 50%.
			</p>
		</dd>
	</dl>
	<dl>
		<dt>
			<p>
				Q: Where would you recommend Museum Grade (UV filtering) & Commercial
				Grade (scratch resistant)?
			</p>
		</dt>
		<dd>
			<p>
				Many clients order
				<a href="/custom-storm-windows/museum-grade">Museum Grade</a>
				for areas where direct sunlight is a concern to protect furniture, art, carpets
				or kids from the damaging effects of the sun's UV rays.
				<a href="/custom-storm-windows/commercial-grade">Commercial Grade</a>
				is generally ordered for areas near the kitchen sink where it will be washed
				or wiped off more often or removed frequently. A pet or child with a sharp
				object will still be able to damage Commercial Grade Indow inserts.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: Will Indow inserts add to my mold problems?</p></dt>
		<dd>
			<p>
				Window mold is generally caused by window condensation. Condensation
				happens when moist air comes into contact with a colder surface, such as
				a window. Indow inserts help prevent window condensation through
				insulating the warm home air from the cold outer windowpane.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: Will the inserts trap condensation?</p></dt>
		<dd>
			<p>
				Indow window inserts should greatly reduce or entirely
				<a href="/resources/case-studies/stop-window-condensation">
					eliminate condensation
				</a>
				on existing single or double-pane windows. The Compression Tube effectively
				stops warm, moist air from reaching the glass. Most clients find that they
				can see outside their kitchens for the first time during dinner in the wintertime.
				There will be times when the acrylic will start to fog up but not nearly
				as severe as the glass would. If condensation is repeatedly appearing inside
				the glass or fogging up the acrylic, then there is most likely an existing
				condition in the wall that should be addressed and remedied.
			</p>
		</dd>
	</dl>
	<dl>
		<dt>
			<p>
				Q: I've had exterior storm windows in the past and wasn't satisfied with
				their performance. Will Indow inserts be able to outperform storm
				windows?
			</p>
		</dt>
		<dd>
			<p>
				Exterior storm windows are designed to protect against rain and,
				sometimes, against strong winds. Indow inserts are designed to insulate
				windows from cold and heat, reduce sound transmission, and provide
				better thermal and acoustical insulation than traditional storm windows.
				If you've used exterior storm windows in the past, you'll easily be able
				to feel/see the difference with Indow inserts.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: What is “R-value”?</p></dt>
		<dd>
			<p>
				R-value measures the thermal resistance of walls, windows, and other
				parts of a building. An Indow insert combined with a single pane window
				provides 94% of the thermal insulation of a standard double pane window.
				<a href="/solutions/window-insulation">Look closer</a>
			</p>
			<p class="italic">
				*We want to ensure our inserts help solve your noise problem. Overall
				noise reduction depends on the amount of noise coming through your
				walls, ceilings, floors, doors, and existing windows. Noise reduction
				will be less when placed over double-pane windows. Read our full
				<a href="/noise-disclaimer">Noise Reduction Sheet</a>
				to learn more.
			</p>
		</dd>
	</dl>
	<nav>
		<ul class="flex list-none flex-col items-center">
			<li>
				<a href="#toc">Return to FAQ Table of Contents</a>
			</li>
			<li>
				<a href="/how-to/get-indow-inserts">Get A Free Estimate</a>
			</li>
		</ul>
	</nav>
</Section>

<hr />

<Section backgroundColor="white" id="durability">
	<h2>Durability</h2>
	<dl>
		<dt><p>Q: How long do Indow inserts last?</p></dt>
		<dd>
			<p>
				Indow inserts have no moving parts, the acrylic will remain transparent,
				and the silicone tubing is extremely resilient, allowing Indow to offer
				a limited lifetime warranty. Over time, the ABS (rigid plastic) part of
				our white tubing can naturally yellow on the side facing outdoors, but
				it does not impact the structural integrity of the insert. The rigid
				plastic part of brown and black compression tubing may slightly fade
				over time. Check out the
				<a href="/warranties/residential-lifetime-warranty">
					Indow residential warranty
				</a>
				or
				<a href="/warranties/commercial-warranty">
					Indow commercial warranty
				</a>
				for more detailed information. (
				<a
					href="/static/pdfs/IW-Care-Guide-Limited-Warranty_05-01-14.pdf"
					target="_blank"
				>
					See here
				</a>
				for Indow warranty information for purchases prior to 1/1/15)
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: Doesn't acrylic turn yellow over time?</p></dt>
		<dd>
			<p>
				The acrylic we use to make our inserts does not yellow, crack or
				discolor from exposure to UV radiation or simple aging.
			</p>
			<p>
				You may see some color shifts over time on the rigid plastic piece
				connected to the tubing. If this does occur, it will be on the side
				facing outdoors. It is a natural occurrence and does not impact the
				structural integrity of the insert.
			</p>
			<p>
				The acrylic glazing is protected in your warranty against any yellowing
				or distortion that would disrupt your view.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: Doesn't acrylic scratch very easily?</p></dt>
		<dd>
			<p>
				If not cared for properly, especially with storage and cleaning, acrylic
				can scratch. Many scratches can be buffed out with an acrylic repair
				kit. If scratching is a concern, we offer a
				<a href="/custom-storm-windows/commercial-grade">Commercial Grade</a>
				Indow that has a durable, scratch resistant coating. In practice, we have
				had very, very few customers ever express any issues with scratching.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: How durable is the edging and will it fade?</p></dt>
		<dd>
			<p>
				The insert's frame is constructed from two elements. (1) The compression
				silicone tubing which rests against the window frame and (2) the rigid
				ABS plastic which holds the acrylic.
			</p>
			<p>
				The silicone tubing is similar to the material used to make oven mitts
				and cooking spatulas. It was chosen for its durability and it will not
				fade from exposure to UV or be affected by heat or cold.
			</p>
			<p>
				The rigid ABS plastic attached to the tubing may shift color over time,
				more likely with white than with brown or black. If this does occur, it
				will only be on the side facing outdoors. It is a natural occurrence and
				does not impact the structural integrity of the insert.
			</p>
		</dd>
	</dl>
	<dl>
		<dt>
			<p>Q: Will the acrylic crack?</p>
		</dt>
		<dd>
			<p>
				The acrylic glazing has a high strength to weight ratio and will not
				crack if handled properly.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: Will the inserts warp or bow under high temperatures?</p></dt>
		<dd>
			<p>
				The acrylic is fine up to a normal operating temperature of 160 degrees
				F. Even under exposure to hot water in environments such as home showers
				(where temperature goes to 120 F), the inserts will continue to perform
				without issue.
			</p>
		</dd>
	</dl>
	<nav>
		<ul class="flex list-none flex-col items-center">
			<li>
				<a href="#toc">Return to FAQ Table of Contents</a>
			</li>
			<li>
				<a href="/how-to/get-indow-inserts">Get A Free Estimate</a>
			</li>
		</ul>
	</nav>
</Section>

<hr />

<Section backgroundColor="white" id="wheretheywork">
	<h2>Where They Work and Where They Do Not Work</h2>
	<dl>
		<dt>
			<p>Q: Which types of windows/frames will Indow inserts work with?</p>
		</dt>
		<dd>
			<p>
				Even if your window is not perfectly square, our measurement process
				allows us to create the exact shape of your window for a precise fit.
				Our proprietary software was designed to check the accuracy of
				measurements as they are entered.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: What types of windows will Indow inserts NOT work in?</p></dt>
		<dd>
			<p>
				Our inserts always mount inside existing windows, so are not appropriate
				as the primary windows (in screen porches, for example). We also do not
				install our inserts inside sliding glass doors. We do not recommend
				Indow inserts for Recreational Vehicles. Due to the flexing of the
				vehicles when they go over bumps, etc, the inserts might fall out.
			</p>
		</dd>
	</dl>
	<dl>
		<dt>
			<p>Q: What's the largest window size Indow inserts will work in?</p>
		</dt>
		<dd>
			<p>
				Size restrictions have many variables according to width, height, window
				function and air leakage. If you have windows larger than the sizes we
				currently offer (6×8 for Standard, Acoustic, Commercial, & Museum; 4×8
				for Privacy & Sleep Panel) please
				<a href="/how-to/get-indow-inserts"
					>contact us for a free consultation</a
				>
				. We may be able to meet your needs by introducing
				<a href="/about-indow/how-to-order/mullions">mullions</a>
				into your large windows.
			</p>
		</dd>
	</dl>
	<dl>
		<dt>
			<p>Q: My windows are not standard size; can I still use Indow inserts?</p>
		</dt>
		<dd>
			<p>
				Yes, each Indow insert is custom made to fit each window. This means our
				window inserts work for out of square windows and
				<a href="/solutions/odd-shaped-window-inserts">odd-shaped windows</a>
				.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: Will Indow inserts work with curved windows?</p></dt>
		<dd>
			<p>
				Indow inserts can accommodate all sort of special shapes on a flat
				plane, such as circles, arches, polygons, and more. Indow is not made
				for curved windows on a radius, such as in a circular tower that need to
				be installed on a curved plane. If there is room in the sill to install
				the insert flat, we may still be able to assist. If you are unsure about
				your situation please contact us for a free consultation.
			</p>
		</dd>
	</dl>
	<dl>
		<dt>
			<p>Q: My windows are out of square. Will Indow inserts still work?</p>
		</dt>
		<dd>
			<p>
				Indow inserts work very well with out of square windows without any
				modification to the windows themselves.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: Can Indow inserts be installed on doors?</p></dt>
		<dd>
			<p>
				French doors, sliding doors, and other doors are are not appropriate
				applications for Indow. The frames for the door are usually not ideal
				for Indow. Also, because of the size and weight, window inserts placed
				into door openings would not be easily removed for egress. If you have a
				door opening that has been permanently sealed, and have a door frame
				that provides at least ⅝” of flat surface perpendicular to the door all
				the way around the perimeter of the door, contact us for a
				<a href="/how-to/get-indow-inserts">consultation about your space</a>
				. Some special circumstances may work with the use of
				<a href="/about-indow/how-to-order/mullions">mullions</a>
				.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: Will Indow inserts work with my blinds and curtains?</p></dt>
		<dd>
			<p>
				Indow inserts will work with various window treatments if the curtains
				or blinds are mounted to the face of the window frame rather than to the
				inside. Watch this installation of
				<a
					href="https://www.youtube.com/watch?v=0mK5ak-RNJo"
					target="_blank"
					rel="external"
				>
					Indow inserts and blinds
				</a>
				.
			</p>
		</dd>
	</dl>
	<dl>
		<dt>
			<p>Q: Is there any construction required for installing Indow inserts?</p>
		</dt>
		<dd>
			<p>
				Aside from the safety chains, which install with one screw, there are no
				additional nails, adhesives, or screws involved in the installation and
				no construction required to the window frame itself.
			</p>
			<p>
				If you're still not sure if your window will work with Indow window
				inserts, the best thing to do is
				<a href="/how-to/get-indow-inserts"
					>contact one of our Fit Specialists.</a
				>
				You submit rough measurements and they can talk through any concerns you
				have.
			</p>
		</dd>
	</dl>
	<dl>
		<dt>
			<p>Q: Can I use an Indow insert to seal up my fireplace?</p>
		</dt>
		<dd>
			<p>
				Indow inserts are not rated for exposure to temperatures above 175
				degrees. Installing an insert over a fireplace also presents a hazard if
				it's used in conjunction with the fireplace. For that reason we do not
				manufacture to seal up fireplace openings.
			</p>
		</dd>
	</dl>
	<nav>
		<ul class="flex list-none flex-col items-center">
			<li>
				<a href="#toc">Return to FAQ Table of Contents</a>
			</li>
			<li>
				<a href="/how-to/get-indow-inserts">Get A Free Estimate</a>
			</li>
		</ul>
	</nav>
</Section>

<hr />

<Section backgroundColor="white" id="purchasing">
	<h2>Purchasing / How To Order</h2>
	<dl>
		<dt><p>Q: I'm interested in Indow inserts! What is the next step?</p></dt>
		<dd>
			<p>
				To get you an initial estimate, we'll need some rough measurements and
				photos of the windows you want to insulate. One of our expert Fit
				Specialists will review your windows with you to ensure that our inserts
				will achieve the results you're looking for. Then we'll set you up to
				take production-quality measurements with our Laser Measure Kit before
				producing your inserts.
			</p>
			<p>
				To speak with a Fit Specialist, <a href="/how-to/get-indow-inserts"
					>enter your contact information here</a
				> and we'll be in touch.
			</p>
			<p>
				You can also call us <a href="tel:503-284-2260">503-284-2260</a> or
				email us at
				<a href="mailto:comfort@indowwindows.com">comfort@indowwindows.com</a>
				.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: How much do Indow inserts cost?</p></dt>
		<dd>
			<p>
				The cost of an insert depends on the size and shape of your window and
				the <a href="/custom-storm-windows">type of grade</a> you choose. Pricing
				for most Indow inserts ranges from $30/sf to $39/sf. This does not include
				sales tax or shipping charges, which vary by region and order size.
			</p>
			<p>
				Consult with one of our Fit Specialists to <a
					href="/how-to/get-indow-inserts">get a Free Estimate</a
				> with our best pricing.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: Is financing available for purchasing Indow inserts?</p></dt>
		<dd>
			<p>
				Yes. Financing is available for all clients. Financing is available
				through PayPal. You can apply at the time of payment (for your final
				invoice).
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: How long does it take to receive my Indow window inserts?</p></dt>
		<dd>
			<p>
				The production time for your custom Indow insert(s) are affected by
				current demand at the time you place your order. Inserts come fully
				assembled and ready to be installed and our team of Indow insert experts
				will be standing by if you have any questions.
			</p>
		</dd>
	</dl>
	<nav>
		<ul class="flex list-none flex-col items-center">
			<li>
				<a href="#toc">Return to FAQ Table of Contents</a>
			</li>
			<li>
				<a href="/how-to/get-indow-inserts">Get A Free Estimate</a>
			</li>
		</ul>
	</nav>
</Section>

<hr />

<Section backgroundColor="white" id="shipping">
	<h2>Shipping & Receiving</h2>
	<dl>
		<dt><p>Q: How much does it cost to ship my inserts?</p></dt>
		<dd>
			<p>
				Shipping charges vary by region, order volume, and the size of your
				inserts. Because our custom-made inserts are the size of your windows,
				shipping fees often run between $300-$400, but can be much higher.
				However, shipping multiple inserts in one package will drastically
				reduce the cost of shipping per insert, which is why we recommend
				ordering multiple inserts together. Your <a
					href="/how-to/get-indow-inserts">Fit Specialist</a
				> can get you a shipping estimate and help you get the most value from your
				order.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: How big of a box do I receive for window inserts?</p></dt>
		<dd>
			<p>
				The size of the box depends on the size of your window insert. Your
				inserts will be delivered in a crate or tote depending on how many
				inserts are in your order. Each insert will be packaged in a cardboard
				box secured down with plastic straps. For detailed information on a
				possible order size, speak with an Indow representative.
			</p>
		</dd>
	</dl>
	<dl>
		<dt>
			<p>
				Q: If my shipment is being delivered via UPS Freight, will they bring
				the package into my home?
			</p>
		</dt>
		<dd>
			<p>
				UPS will deliver your package to your home, but not inside. The UPS
				driver will drop off your package either in your driveway or curbside.
			</p>
			<p>
				Please note that the UPS Freight division is now owned by TForce. They
				are still using UPS tracking links, but TForce reps handle scheduling
				and delivery.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: What tools do I need to unpackage my inserts?</p></dt>
		<dd>
			<p>
				For UPS Ground packages or totes, just a heavy duty pair of scissors. If
				you receive a crate you'll also need a flat head screwdriver or staple
				remover. We advise against using box cutters to ensure that your order
				stays safe while you unpackage it.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: How can I best dispose of the packaging material?</p></dt>
		<dd>
			<p>
				Nearly all of the Indow packaging is recyclable! We use cardboard so
				that it can be broken down and put into your recycling container. The
				wooden base that some larger orders arrive on can be reused or recycled.
				The only thing you cannot recycle are the plastic straps that hold down
				the cardboard box.
			</p>
		</dd>
	</dl>
	<dl>
		<dt>
			<p>Q: Is the tracking for delivery to my home or the local UPS hub?</p>
		</dt>
		<dd>
			<p>
				If your insert is being delivered by UPS Ground, the “Scheduled
				Delivery” is when the package will arrive at your home.
			</p>
			<p>
				If your insert is being delivered by UPS Freight, the “Scheduled
				Delivery” is when the package will arrive at the UPS hub nearest to your
				home. Once it is at the hub, UPS will contact you to set a home
				delivery.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: My tracking is not active. Why?</p></dt>
		<dd>
			<p>
				We have created the shipping label for your package, and it may have
				been picked up by the shipping company, but it hasn't reached their hub
				or been updated by their system yet. This can take up to 72 hours to be
				updated.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: What should I do about damaged packages?</p></dt>
		<dd>
			<p>
				If the packaging is damaged, accept the shipment, but take pictures of
				the packaging before removing any of the inserts. Keep all packaging
				until the inserts have been installed. If you find any damaged inserts
				please take pictures right away. Any issues must be reported to Indow
				within 30 days of receipt. Please contact your Fit Specialist
				immediately and
				<a href="/claim-submission-customer">submit a claim</a>
				.
			</p>
		</dd>
	</dl>
	<nav>
		<ul class="flex list-none flex-col items-center">
			<li>
				<a href="#toc">Return to FAQ Table of Contents</a>
			</li>
			<li>
				<a href="/how-to/get-indow-inserts">Get A Free Estimate</a>
			</li>
		</ul>
	</nav>
</Section>

<hr />

<Section backgroundColor="white" id="upkeepandcare">
	<h2>Upkeep and Care</h2>
	<dl>
		<dt>
			<p>
				Q: I see fog or moisture collecting on the inside of Indow inserts, what
				do I do?
			</p>
		</dt>
		<dd>
			<p>
				Indow inserts almost always dramatically reduce condensation on the
				original single- (or double-) pane windows. If condensation does occur,
				it often indicates a problem with moisture build up in the walls.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: How do I clean my Indow inserts?</p></dt>
		<dd>
			<p>
				Indow inserts can be
				<a
					href="https://www.youtube.com/watch?v=5x1QFndaSjk"
					target="_blank"
					rel="external"
				>
					easily cleaned
				</a>
				with a microfiber cloth with or without a mild soap mixture. See our
				<a href="/about-indow/care">Care Guide</a>
				.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: Can I repair scratches?</p></dt>
		<dd>
			<p>
				In some cases, it may be possible to lessen the appearance of scratches
				by using a plastic polish such as Novus 2 or 3 which can be found at
				most major hardware stores. Be careful to follow the manufacturer's
				directions as the incorrect use may damage your Indow insert.
			</p>
		</dd>
	</dl>
	<dl>
		<dt><p>Q: How do I store my Indow inserts?</p></dt>
		<dd>
			<p>
				Many Indow inserts can be left in place year round. If an Indow insert
				is removed for an extended period, proper storage is essential. You can
				make storage sleeves, but be sure to consider the abrasiveness of the
				material to avoid scratching. The best option is to store them flat on
				the floor with the largest on the bottom, compression tubes touching, no
				more than 3 high to prevent bowing or warping. Do not lean inserts and
				store below 160 degrees. See
				<a
					href="https://www.youtube.com/watch?v=y-7defdt0Pw"
					target="_blank"
					rel="external"
				>
					video on storage
				</a>
				and
				<a
					href="/static/pdfs/IW-Storing-Indow-Window-Inserts-10-16-19.pdf"
					target="_blank"
				>
					full storage details
				</a>
				. See more insert care details on our
				<a href="/about-indow/care/installation-resources">
					customer resource page
				</a>
				.
			</p>
		</dd>
	</dl>
	<nav>
		<ul class="flex list-none flex-col items-center">
			<li>
				<a href="#toc">Return to FAQ Table of Contents</a>
			</li>
			<li>
				<a href="/how-to/get-indow-inserts">Get A Free Estimate</a>
			</li>
		</ul>
	</nav>
</Section>

<hr />

<Section backgroundColor="white" id="odors">
	<h2>Odors</h2>
	<dl>
		<dt>
			<p>Q: My new Indow window inserts have a smell? What should I do?</p>
		</dt>
		<dd>
			<p>
				Sometimes Indow window inserts have a “new car smell” that will
				dissipate. If you find the smell bothersome, you can temporarily store
				your inserts in a large, well-ventilated room until the odor is gone.
			</p>
		</dd>
	</dl>

	<dl>
		<dt>
			<p>
				Q: Will Indow hold my inserts for a few weeks so any “new car smell” can
				dissipate at the factory?
			</p>
		</dt>
		<dd>
			<p>
				Yes. Indow is happy to hold inserts for several weeks before shipping
				them. Just make this preference clear during the purchase process.
			</p>
		</dd>
	</dl>
	<nav>
		<ul class="flex list-none flex-col items-center">
			<li>
				<a href="#toc">Return to FAQ Table of Contents</a>
			</li>
			<li>
				<a href="/how-to/get-indow-inserts">Get A Free Estimate</a>
			</li>
		</ul>
	</nav>
</Section>

<hr />

<Section backgroundColor="white" id="taxcredit">
	<h2>Tax Credit Information</h2>
	<dl>
		<dt><p>Q: Can I get tax credit for using Indow inserts?</p></dt>
		<dd>
			<p>
				Commercial building owners and developers might be eligible for a
				Federal Rehabilitation Tax Credit (also known as the Historic Tax
				Credit). If Indow inserts are used to retrofit and restore historic
				windows instead of replace them, your building may qualify. Learn more
				about
				<a href="/commercial-storm-windows/historic-tax-credit">tax credits</a>
				.
			</p>
		</dd>
	</dl>
	<nav>
		<ul class="flex list-none flex-col items-center">
			<li>
				<a href="#toc">Return to FAQ Table of Contents</a>
			</li>
			<li>
				<a href="/how-to/get-indow-inserts">Get A Free Estimate</a>
			</li>
		</ul>
	</nav>
</Section>

<GetAFreeEstimate title="Get A Free Estimate" backgroundColor="blue" />

<style>
	dl {
		margin-bottom: 1.5rem;
		width: inherit;
	}
	dl p {
		text-align: left;
	}
	dt {
		font-size: 1.125rem;
		line-height: 1.75rem;
		font-weight: bold;
	}
	h2 {
		width: inherit;
		text-align: left;
	}
	dt,
	dd {
		justify-self: start;
	}
	p {
		text-align: left;
		justify-self: start;
	}
</style>
